import { Box, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Switch, Tooltip, Typography } from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';
import { Form } from '../../../shared/forms';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { VTextField, VTextFieldCurrency } from '../../../shared/forms';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import { formatDuration } from '../../../shared/helpers/formatDuration';
import { LayoutRegister } from '../../../shared/layouts';
import { ICreateService, IServiceView, IUpdateService, serviceService } from '../../../shared/services';
import { useRegisterForm } from '../../../shared/hooks/useRegisterForm';

const DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240, 300, 360];

const schema = Yup.object().shape({
  description: Yup.string().required(),
  amount: Yup.number().required(),
  serviceDuration: Yup.number().min(15).max(360).required(),
});

interface IProps {
  businessId: number;
  entityId?: number;
  refetch: () => void;
  onCloseEdit: () => void;
}

export const BusinessServicesRegister: React.FC<IProps> = ({ businessId, entityId: serviceId, refetch, onCloseEdit }) => {
  const [serviceDuration, setServiceDuration] = useState(30);
  const [requiresPreApproval, setRequiresPreApproval] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const { formRef, isLoadingData, isSaving, create, update, LoadingSkeleton } = useRegisterForm({
    queryKey: 'service',
    queryFn: (id) => serviceService.getById(id),
    createFn: (dto: ICreateService) => serviceService.create(dto),
    updateFn: (dto: IUpdateService) => serviceService.update(dto),
    editId: serviceId,
    onSuccess: () => { refetch(); onCloseEdit(); },
    createMessage: 'Serviço criado',
    updateMessage: 'Serviço editado',
    onLoadData: (data: IServiceView) => {
      setServiceDuration(data.serviceDuration ?? 30);
      setRequiresPreApproval(data.requiresPreApproval ?? false);
      setIsActive(data.isActive ?? true);
    },
  });

  const handleSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        formRef.current?.setErrors({});

        await schema.validate(
          { description: data.description, amount: data.amount, serviceDuration },
          { abortEarly: false }
        );

        const base = {
          businessId,
          description: data.description,
          amount: Number(data.amount),
          serviceDuration,
          requiresPreApproval,
          isActive,
        };

        if (serviceId) {
          update({ ...base, id: serviceId } as IUpdateService);
        } else {
          create(base as ICreateService);
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        }
      }
    },
    [serviceDuration, requiresPreApproval, isActive, businessId, serviceId, formRef, create, update]
  );

  if (isLoadingData && serviceId) {
    return <LoadingSkeleton />;
  }

  return (
    <Form ref={formRef} onSubmit={handleSubmit}>
      <LayoutRegister header='Serviço' isSubmit isLoading={isSaving} saveButtonDataTestId='btn-save-service'>
        <Box display='flex' flexDirection='column' gap={2}>
          <VTextField name='description' label='Descrição' required />
          <VTextFieldCurrency name='amount' label='Valor (R$)' required />
          <FormControl fullWidth>
            <InputLabel>Duração (minutos)</InputLabel>
            <Select
              value={serviceDuration}
              label='Duração (minutos)'
              onChange={(e) => setServiceDuration(Number(e.target.value))}
            >
              {DURATION_OPTIONS.map((d) => {
                const hours = d >= 60 ? ` (${formatDuration(d).replace(' ', '')})` : '';
                return <MenuItem key={d} value={d}>{d} min{hours}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={isActive ? 'Ativo' : 'Inativo'}
          />
          <Box display='flex' alignItems='center'>
            <FormControlLabel
              control={
                <Switch checked={requiresPreApproval} onChange={(e) => setRequiresPreApproval(e.target.checked)} />
              }
              label='Exigir confirmação do profissional'
            />
            <Tooltip
              title={
                <Typography variant='body2'>
                  Quando ativado, agendamentos com este serviço ficarão com status &quot;Pendente&quot; até que o profissional aprove.
                  <br /><br />
                  <strong>Exemplo:</strong> pintar cabelo — o profissional precisa confirmar pois pode precisar comprar material, fazer teste de mecha, etc.
                  <br /><br />
                  O profissional pode aprovar ou rejeitar pelo app (clicando no agendamento na agenda).
                </Typography>
              }
            >
              <IconButton size='small' tabIndex={-1}>
                <HelpIcon fontSize='small' color='action' />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </LayoutRegister>
    </Form>
  );
};
